import React, { useMemo, useState } from 'react'
import { CompanyProfileEnrichment } from '../../setup/CompanyProfileEnrichment'
import type { CompanyProfileConfirmation } from '../../../services/companyEnrichment'
import {
  createOrganization,
  getSystemOrganizationDeleteImpact,
  runSystemOrganizationLifecycleAction,
  updateOrganization,
  type DeleteImpact,
} from '../../../services/access'
import type {
  AccessTenant,
  Organization,
  OrganizationBranding,
  OrganizationPayload,
  OrganizationRelationship,
  OrganizationStatus,
  OrganizationType,
  OrganizationUnit,
} from '../../../types/access'
import { formatOrganizationLabel, formatTenantLabel } from '../../../utils/displayLabels'
import { brandColors, brandingLogo, brandingName, brandingOrgId, formatDate, formatOrgType, orgTypeOptions } from './adminPanelUtils'
import { ConfirmDialog, DetailDrawer, EmptyState, EntityActionMenu, ErrorBanner, FormModal, disabledReason } from './AdminUiPrimitives'
import { useI18n } from '../../../i18n/I18nContext'

interface OrganizationsPanelProps {
  organizations: Organization[]
  brandingRows: OrganizationBranding[]
  organizationUnits: OrganizationUnit[]
  organizationRelationships: OrganizationRelationship[]
  tenant?: AccessTenant | null
  tenantId?: string
  canManageOrganizations: boolean
  onRefresh: () => Promise<void>
  onEditBranding?: (orgId: string) => void
}

const statusOptions: OrganizationStatus[] = ['pending', 'active', 'suspended', 'archived', 'pending_deletion', 'deleted']

const initialForm: OrganizationPayload = {
  name: '',
  slug: '',
  orgType: 'agency',
  status: 'active',
}

export const OrganizationsPanel: React.FC<OrganizationsPanelProps> = ({
  organizations,
  brandingRows,
  organizationUnits,
  organizationRelationships,
  tenant,
  tenantId,
  canManageOrganizations,
  onRefresh,
  onEditBranding,
}) => {
  const { t } = useI18n()
  const [filter, setFilter] = useState<'all' | OrganizationType>('all')
  const [showCreate, setShowCreate] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null)
  const [viewingOrg, setViewingOrg] = useState<Organization | null>(null)
  const [form, setForm] = useState<OrganizationPayload>(initialForm)
  const [profileConfirmation, setProfileConfirmation] = useState<CompanyProfileConfirmation>()
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState<{ org: Organization; impact?: DeleteImpact } | null>(null)

  const visibleOrganizations = useMemo(
    () => (filter === 'all' ? organizations : organizations.filter((org) => org.org_type === filter)),
    [filter, organizations],
  )
  const rootOuForOrg = (orgId: string) => organizationUnits.find((unit) => unit.org_id === orgId && unit.ou_type === 'root')
  const relationshipCountForOrg = (orgId: string) => organizationRelationships.filter((relationship) => (
    relationship.source_org_id === orgId || relationship.target_org_id === orgId
  )).length

  const openCreate = () => {
    setProfileConfirmation(undefined)
    setEditingOrg(null)
    setForm(initialForm)
    setShowCreate(true)
    setMessage('')
    setError('')
  }

  const openEdit = (org: Organization) => {
    setProfileConfirmation(undefined)
    setViewingOrg(null)
    setEditingOrg(org)
    setForm({
      name: org.name,
      slug: org.slug || '',
      orgType: org.org_type,
      status: org.status || 'active',
    })
    setShowCreate(true)
    setMessage('')
    setError('')
  }

  const closeForm = () => {
    setProfileConfirmation(undefined)
    setShowCreate(false)
    setEditingOrg(null)
    setForm(initialForm)
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canManageOrganizations) return
    if (!form.name.trim()) {
      setError(t('Organization name is required.'))
      return
    }
    setSaving(true)
    setError('')
    setMessage('')
    try {
      const payload = {
        name: form.name.trim(),
        slug: form.slug?.trim() || undefined,
        status: form.status,
      }
      if (editingOrg) {
        await updateOrganization(editingOrg.id, payload)
        setMessage(t('Organization updated.'))
      } else {
        await createOrganization({ ...payload, orgType: form.orgType, businessProfileConfirmation: profileConfirmation })
        setMessage(t('Organization created. Tenant role defaults and the root OU are created by the backend when needed.'))
      }
      await onRefresh()
      closeForm()
    } catch (err: any) {
      setError(err?.response?.data?.message || t('Unable to save organization.'))
    } finally {
      setSaving(false)
    }
  }

  const runLifecycle = async (org: Organization, action: 'suspend' | 'archive' | 'restore' | 'delete') => {
    if (!canManageOrganizations) return
    setSaving(true)
    setError('')
    setMessage('')
    try {
      if (action === 'delete') {
        const impact = await getSystemOrganizationDeleteImpact(org.id)
        if (!impact.canDelete) {
          setDeleteConfirm({ org, impact })
          setMessage(t('Delete is blocked by business history. Archive is recommended.'))
          return
        }
        setDeleteConfirm({ org, impact })
        return
      }
      await runSystemOrganizationLifecycleAction(org.id, action, `${action} requested from Systems Admin`)
      setMessage(t('Organization {action} complete.', { action: t(action) }))
      await onRefresh()
    } catch (err: any) {
      const data = err?.response?.data
      if (data?.error === 'delete_blocked') {
        setDeleteConfirm({ org, impact: { canDelete: false, recommendedAction: 'archive', dependencies: data.dependencies || [] } })
      }
      setError(data?.message || err?.response?.data?.message || t('Unable to {action} organization.', { action: t(action) }))
    } finally {
      setSaving(false)
    }
  }

  const confirmDelete = async () => {
    if (!deleteConfirm) return
    setSaving(true)
    setError('')
    try {
      await runSystemOrganizationLifecycleAction(deleteConfirm.org.id, 'delete', 'safe delete confirmed from Systems Admin')
      setMessage(t('Organization safely deleted.'))
      setDeleteConfirm(null)
      await onRefresh()
    } catch (err: any) {
      const data = err?.response?.data
      setError(data?.message || t('Unable to delete organization.'))
      if (data?.error === 'delete_blocked') {
        setDeleteConfirm({ org: deleteConfirm.org, impact: { canDelete: false, recommendedAction: 'archive', dependencies: data.dependencies || [] } })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stack" data-testid="admin-organizations-panel">
      <div className="admin-panel-toolbar">
        <div className="stack" style={{ gap: '.25rem' }}>
          <h3 style={{ margin: 0 }}>{t('Tenant organization directory')}</h3>
          <p className="text-muted">{t('Manage companies, agencies, subcontractors, and platform-owner boundaries in the workforce network.')}</p>
        </div>
      </div>

      <div className="admin-panel-toolbar">
        <div className="inline-group">
          <button type="button" className={`admin-filter ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>{t('All')}</button>
          <button type="button" className={`admin-filter ${filter === 'company' ? 'active' : ''}`} onClick={() => setFilter('company')}>{t('Companies')}</button>
          <button type="button" className={`admin-filter ${filter === 'agency' ? 'active' : ''}`} onClick={() => setFilter('agency')}>{t('Agencies')}</button>
          <button type="button" className="admin-filter" disabled title={t('Subcontractor organization type is not connected yet.')}>{t('Subcontractors')}</button>
          <button type="button" className="admin-filter" disabled title={t('Archived filtering is handled through status actions for now.')}>{t('Archived')}</button>
          <button type="button" className={`admin-filter ${filter === 'platform_owner' ? 'active' : ''}`} onClick={() => setFilter('platform_owner')}>{t('Platform owners')}</button>
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!canManageOrganizations}
          title={disabledReason(canManageOrganizations, t('You can view organizations, but creating one requires organization management access.'))}
          onClick={openCreate}
        >
          {t('+ Create Tenant Organization')}
        </button>
      </div>

      {!canManageOrganizations ? (
        <p className="helper">{t('Organization create/edit requires tenant RBAC management permission in the current backend routes.')}</p>
      ) : null}
      {message ? <p className="helper">{message}</p> : null}
      <ErrorBanner message={error} />

      <FormModal
        title={editingOrg ? t('Edit tenant organization') : t('Create tenant organization')}
        description={t('Use Network Relationships to onboard an Associated Client Company. This form creates only a tenant-local organization boundary.')}
        isOpen={showCreate}
        onClose={closeForm}
        size="lg"
      >
        <form className="stack" onSubmit={handleSubmit}>
          <CompanyProfileEnrichment key={editingOrg?.id || 'new'} orgId={editingOrg?.id} onConfirmDraft={confirmation => {
            setProfileConfirmation(confirmation)
            const name = confirmation.fields.legalName || confirmation.fields.tradingName
            if (typeof name === 'string') setForm(previous => ({ ...previous, name }))
          }} />
          <div className="admin-form-grid">
            <div className="form-row">
              <label htmlFor="org-name">{t('Name')}</label>
              <input id="org-name" className="input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
            </div>
            <div className="form-row">
              <label htmlFor="org-slug">{t('Slug')}</label>
              <input id="org-slug" className="input" value={form.slug || ''} onChange={(event) => setForm({ ...form, slug: event.target.value })} />
            </div>
            <div className="form-row">
              <label htmlFor="org-type">{t('Type')}</label>
              <select id="org-type" className="input" value={form.orgType} disabled={Boolean(editingOrg)} onChange={(event) => setForm({ ...form, orgType: event.target.value as OrganizationType })}>
                {orgTypeOptions.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
              </select>
            </div>
            <div className="form-row">
              <label htmlFor="org-status">{t('Status')}</label>
              <select id="org-status" className="input" value={form.status || 'active'} onChange={(event) => setForm({ ...form, status: event.target.value as OrganizationStatus })}>
                {statusOptions.map((status) => <option key={status} value={status}>{t(status)}</option>)}
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('Saving...') : t('Save tenant organization')}</button>
        </form>
      </FormModal>

      {organizations.length === 0 ? (
        <EmptyState
          title={t('No organizations in this tenant yet')}
          description={canManageOrganizations
            ? t('Create a platform owner, agency, or company. The backend will ensure tenant defaults and a root OU for new organizations.')
            : t('Your access context does not include any organizations. Ask a platform admin to grant organization-scoped access.')}
        />
      ) : null}

      <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
        <table className="workspace-table">
          <thead><tr><th>{t('Name')}</th><th>{t('Tenant')}</th><th>{t('Type')}</th><th>{t('Status')}</th><th>{t('Root OU')}</th><th>{t('Relationships')}</th><th>{t('Branding')}</th><th>{t('Actions')}</th></tr></thead>
          <tbody>
            {visibleOrganizations.map((org) => {
              const branding = brandingRows.find((row) => brandingOrgId(row) === org.id)
              const rootOu = rootOuForOrg(org.id)
              const relationshipCount = relationshipCountForOrg(org.id)
              return (
                <tr key={org.id}>
                  <td>{org.name}<div className="helper">{org.slug || formatOrganizationLabel(org)}</div></td>
                  <td>
                    {formatTenantLabel(tenant, org.tenant_id || tenantId)}
                    {tenant?.slug ? <div className="helper">{t('Slug')}: {tenant.slug}</div> : null}
                  </td>
                  <td>
                    <span className="status-pill">{t(formatOrgType(org.org_type))}</span>
                    <div className="helper">
                      {org.org_type === 'platform_owner' ? t('Platform owner/admin boundary') : org.org_type === 'agency' ? t('Staffing agency') : t('Client company')}
                    </div>
                  </td>
                  <td><span className="status-pill">{t(org.status || 'active')}</span></td>
                  <td>{rootOu?.name || <span className="admin-disabled-callout">{t('Root OU not visible')}</span>}</td>
                  <td>{relationshipCount}</td>
                  <td>
                    <div className="admin-brand-lockup admin-brand-lockup--compact">
                      {brandingLogo(branding) ? <img src={brandingLogo(branding)} alt="" className="admin-brand-logo admin-brand-logo--sm" /> : (
                        <div className="admin-brand-logo admin-brand-logo--sm admin-brand-logo--fallback">{brandingName(branding, org.name).slice(0, 1).toUpperCase()}</div>
                      )}
                      <div>
                        <strong>{brandingName(branding, org.name)}</strong>
                        <div className="admin-color-hints">
                          {brandColors(branding).map((color) => <span key={color} className="admin-color-swatch" style={{ background: color }} title={color} />)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <EntityActionMenu>
                      <button type="button" className="btn btn-ghost" onClick={() => setViewingOrg(org)}>{t('View details')}</button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={!canManageOrganizations}
                        title={disabledReason(canManageOrganizations, t('You can view this organization, but editing requires organization management access.'))}
                        onClick={() => openEdit(org)}
                      >{t('Edit')}</button>
                      {onEditBranding ? <button type="button" className="btn btn-ghost" onClick={() => onEditBranding(org.id)}>{t('Edit branding')}</button> : null}
                      <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations || saving} onClick={() => runLifecycle(org, 'suspend')}>{t('Suspend')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations || saving} onClick={() => runLifecycle(org, 'archive')}>{t('Archive')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations || saving} onClick={() => runLifecycle(org, 'restore')}>{t('Restore')}</button>
                      <button type="button" className="btn btn-ghost" disabled={!canManageOrganizations || saving} onClick={() => runLifecycle(org, 'delete')}>{t('Delete if safe')}</button>
                    </EntityActionMenu>
                  </td>
                </tr>
              )
            })}
            {visibleOrganizations.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <EmptyState title={t('No organizations found')} description={t('Change the filter or create an agency, company, or platform-owner organization.')} />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <DetailDrawer
        title={viewingOrg?.name || t('Organization')}
        subtitle={viewingOrg ? `${t(formatOrgType(viewingOrg.org_type))} / ${t(viewingOrg.status || 'active')}` : undefined}
        isOpen={Boolean(viewingOrg)}
        onClose={() => setViewingOrg(null)}
        actions={viewingOrg ? (
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canManageOrganizations}
            title={disabledReason(canManageOrganizations, t('You can view this organization, but editing requires organization management access.'))}
            onClick={() => openEdit(viewingOrg)}
          >{t('Edit')}</button>
        ) : null}
      >
        {viewingOrg ? (
          <div className="stack">
            <p><strong>{t('Slug')}</strong><br />{viewingOrg.slug || '-'}</p>
            <p><strong>{t('Tenant')}</strong><br />{formatTenantLabel(tenant, viewingOrg.tenant_id || tenantId)}</p>
            {tenant?.slug ? <p><strong>{t('Tenant slug')}</strong><br />{tenant.slug}</p> : null}
            <p><strong>{t('Type')}</strong><br />{t(formatOrgType(viewingOrg.org_type))}</p>
            <p><strong>{t('Status')}</strong><br />{t(viewingOrg.status || 'active')}</p>
            <p><strong>{t('Root OU')}</strong><br />{rootOuForOrg(viewingOrg.id)?.name || t('Root OU is not visible in your current access context.')}</p>
            <p><strong>{t('Relationship count')}</strong><br />{relationshipCountForOrg(viewingOrg.id)}</p>
            <p><strong>{t('Created')}</strong><br />{formatDate(viewingOrg.created_at)}</p>
            {(() => {
              const branding = brandingRows.find((row) => brandingOrgId(row) === viewingOrg.id)
              return (
                <div className="admin-brand-lockup">
                  {brandingLogo(branding) ? <img src={brandingLogo(branding)} alt="" className="admin-brand-logo" /> : (
                    <div className="admin-brand-logo admin-brand-logo--fallback">{brandingName(branding, viewingOrg.name).slice(0, 1).toUpperCase()}</div>
                  )}
                  <div>
                    <p><strong>{t('Branding')}</strong><br />{brandingName(branding, viewingOrg.name)}</p>
                    <div className="admin-color-hints">
                      {brandColors(branding).map((color) => <span key={color} className="admin-color-swatch" style={{ background: color }} title={color} />)}
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>
        ) : null}
      </DetailDrawer>
      <ConfirmDialog
        title={deleteConfirm?.impact?.canDelete === false ? t('Delete blocked') : t('Delete organization?')}
        message={deleteConfirm?.impact?.canDelete === false
          ? t('Archive is recommended. Blocking dependencies: {dependencies}.', { dependencies: deleteConfirm.impact.dependencies.map((item) => `${item.type}: ${item.count}`).join(', ') || t('business history exists') })
          : t('Delete {name} only if it has no business history.', { name: deleteConfirm?.org.name || t('this organization') })}
        isOpen={Boolean(deleteConfirm)}
        confirmLabel={deleteConfirm?.impact?.canDelete === false ? t('Archive instead') : t('Delete')}
        destructive={deleteConfirm?.impact?.canDelete !== false}
        disabled={saving}
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => {
          if (!deleteConfirm) return
          if (deleteConfirm.impact?.canDelete === false) {
            runLifecycle(deleteConfirm.org, 'archive')
            setDeleteConfirm(null)
            return
          }
          confirmDelete()
        }}
      />
    </div>
  )
}
